import React, { ReactNode, useContext } from "react";
import TextArea from "../../../components/data_entry/text_area";
import QuestionLabel from "./question_label";
import { RadioGroup, RadioButton, RadioGroupDisplay } from "../../../components/data_entry/radio";
import styled from "styled-components";
import MultiSelect from "../multi_select";
import { Option } from "../question_helpers";
import { SurveyContext, SurveyDisplay } from "../../survey";

const ScaleLabel = styled.div`
  font-weight: ${({ theme }) => theme.font.weight.normal};
  font-size: ${({ theme }) => theme.font.size.body.one};
  margin-bottom: ${({ theme }) => theme.deprecatedSpacing.component.four};
  color: ${({ theme }) => theme.deprecatedColors.primary.dark};
`;

export interface LikertFollowUpMultiSelectAnswer {
  standard: (string | Option | undefined)[];
  writeIn?: string;
}

export const times = <T extends ReactNode>(
  fn: (num: number, index: number) => T,
  start: number,
  end: number
): T[] => {
  let num = start;
  let index = 0;
  const list = [];
  while (num <= end) {
    list[index] = fn(num, index);
    index += 1;
    num += 1;
  }
  return list;
};

const StyledRadioButton = styled(RadioButton)`
  flex-grow: 1;
`;

const LikertExtraQuestion = styled.div`
  margin-top: ${({ theme }) => theme.deprecatedSpacing.component.eight};
`;

export type Scale = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export default function Likert({
  prompt,
  value,
  onChangeValue,
  followUpPrompt = "",
  followUpResponse = "",
  followUpLowScore = false,
  followUpLowPrompt = "",
  followUpLowResponse = "",
  followUpLowQuestion,
  lowScoreThreshold = 4,
  onChangeLowFollowUp = () => undefined,
  followUpHighScore = false,
  followUpHighPrompt = "",
  followUpHighResponse = "",
  highScoreThreshold = 5,
  onChangeHighFollowUp = () => undefined,
  optional = false,
  max = 7,
  maxLabel = "High",
  min = 1,
  minLabel = "Low",
  displayValues,
  description,
  followUpLowDescription,
}: {
  prompt: string;
  value?: Scale;
  onChangeValue: (newValue: Scale) => void;
  followUpPrompt?: string;
  followUpResponse?: string | LikertFollowUpMultiSelectAnswer;
  followUpLowScore?: boolean;
  followUpLowPrompt?: string;
  followUpLowResponse?: string | LikertFollowUpMultiSelectAnswer;
  // Fixing an accessibility issue in this PR and don't want to scope creep too much trying to figure out what the
  // type is supposed to be to avoid the eslint warning around this 'any'. Sincerest shame, Lindsay
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  followUpLowQuestion?: { type: string; [propName: string]: any };
  lowScoreThreshold?: number;
  followUpHighScore?: boolean;
  followUpHighPrompt?: string;
  followUpHighResponse?: string | LikertFollowUpMultiSelectAnswer;
  highScoreThreshold?: number;
  onChangeLowFollowUp?: (
    updatedFollowUpAnswer: string | LikertFollowUpMultiSelectAnswer | undefined
  ) => void;
  onChangeHighFollowUp?: (
    updatedFollowUpAnswer: string | LikertFollowUpMultiSelectAnswer | undefined
  ) => void;
  max?: Scale;
  maxLabel?: string;
  min?: Scale;
  minLabel?: string;
  optional?: boolean;
  displayValues?: string[] | number[];
  description?: string;
  followUpLowDescription?: string;
}) {
  // DEPRECATION: avoid using followUpPrompt in favor of followUpLowPrompt
  //              avoid using followUpResponse in favor of followUpLowResponse
  // this aliasing is here to support pre-existing likert questions
  followUpLowPrompt = followUpLowPrompt || followUpPrompt;
  followUpLowResponse = followUpLowResponse || followUpResponse;
  const { display } = useContext(SurveyContext);

  return (
    <div>
      <QuestionLabel
        questionIsOptional={optional}
        className="question-label"
        label={prompt}
        description={description}
        hideRequiredMarker={display == SurveyDisplay.banner}
        data-test="likert-question-label"
      />
      <ScaleLabel className="scale-label">
        {min}={minLabel} {max}={maxLabel}
      </ScaleLabel>
      <RadioGroup
        name="likert"
        value={value}
        onChange={onChangeValue}
        display={RadioGroupDisplay.buttonGroup}
      >
        {times(
          (num, index) => {
            return (
              <StyledRadioButton
                className="likert-option"
                dataTest="likert-option"
                key={num}
                label={`${displayValues ? displayValues[index] : num}`}
                value={num}
              />
            );
          },
          min,
          max
        )}
      </RadioGroup>
      {followUpLowScore && value && value <= lowScoreThreshold && (
        <LikertExtraQuestion>
          {followUpLowQuestion?.type === "multi_select" && (
            <MultiSelect
              customizations={{
                prompt: followUpLowPrompt,
                description: followUpLowDescription,
                options: followUpLowQuestion.options,
                withOtherOption: followUpLowQuestion.withOtherOption,
                optional: followUpLowQuestion.optional,
              }}
              currentAnswer={
                typeof followUpLowResponse !== "string" ? followUpLowResponse : undefined
              }
              updateAnswer={(newAnswer) => {
                onChangeLowFollowUp(newAnswer);
              }}
              setAbleToSubmit={() => {
                /* No-op. We don't currently support required follow ups for this question. */
              }}
              showValidation={false}
            />
          )}
          {!followUpLowQuestion && (
            <>
              <QuestionLabel
                questionIsOptional={true}
                className="question-label"
                label={followUpLowPrompt}
                description={followUpLowDescription}
              />
              <TextArea
                rows={3}
                onChange={({ currentTarget: { value: updatedResponse } }) =>
                  onChangeLowFollowUp(updatedResponse)
                }
                questionIsOptional={true}
                value={typeof followUpLowResponse === "string" ? followUpLowResponse : undefined}
              />
            </>
          )}
        </LikertExtraQuestion>
      )}
      {followUpHighScore && value && value >= highScoreThreshold && (
        <div>
          <QuestionLabel
            questionIsOptional={false}
            className="question-label"
            label={followUpHighPrompt}
            description={""}
          />
          <TextArea
            rows={3}
            questionIsOptional={false}
            onChange={({ currentTarget: { value: updatedResponse } }) =>
              onChangeHighFollowUp(updatedResponse)
            }
            value={typeof followUpHighResponse == "string" ? followUpHighResponse : undefined}
          />
        </div>
      )}
    </div>
  );
}
